import React, { useState } from 'react';
import { useUser } from '../providers/UserProvider';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { cn } from '../utils';
import ConfirmationModal from './ui/ConfirmationModal';
import { sendRequest } from '../api';
import { Loader2 } from 'lucide-react';

const CANCEL_REASONS = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'missing_features', label: 'Missing features' },
  { value: 'switched_service', label: 'Switched to another service' },
  { value: 'unused', label: 'Not using it anymore' },
  { value: 'customer_service', label: 'Customer service issues' },
  { value: 'too_complex', label: 'Too complex to use' },
  { value: 'low_quality', label: 'Low quality service' },
  { value: 'other', label: 'Other' }
];

const PLAN_TIERS = {
  free: 0,
  starter: 1,
  pro: 2,
  superstar: 3
};

const PricingCard = ({ title, description, price, onSubscribe, isCurrentPlan, planTier, currentPlanTier, onDowngrade, isFree, isPopular, isLoading }) => {
  const getButtonText = () => {
    if (isLoading) {
      return (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          {isCurrentPlan ? 'Processing...' : planTier > currentPlanTier ? 'Upgrading...' : 'Downgrading...'}
        </>
      );
    }
    if (isCurrentPlan) {
      return (
        <span className="flex items-center gap-2">
          {isFree ? 'Basic' : 'Subscribed'} {!isFree && <span className="text-xl">🚀</span>}
        </span>
      );
    }
    return planTier > currentPlanTier ? 'Upgrade' : 'Downgrade';
  };

  const isUpgrade = planTier > currentPlanTier;
  const shouldShowPopular = isPopular && isUpgrade;

  const getButtonVariant = () => {
    if (isCurrentPlan) return 'secondary';
    return isUpgrade ? 'default' : 'outline';
  };

  const getButtonClassName = () => {
    const baseClass = 'w-full rounded-[6px] flex items-center justify-center';
    if (isCurrentPlan) return baseClass;
    if (isUpgrade) {
      return cn(
        baseClass,
        'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg shadow-purple-500/25'
      );
    }
    return baseClass;
  };

  const handleClick = () => {
    if (planTier < currentPlanTier) {
      onDowngrade();
    } else {
      onSubscribe();
    }
  };

  return (
    <div className="relative">
      {/* Outline layer with badge */}
      {shouldShowPopular && (
        <div className="absolute inset-0 -m-6 border border-purple-500/30 z-[1]">
          <Badge className="absolute left-1/2 -translate-x-1/2 -top-3 bg-gradient-to-r from-indigo-500 to-pink-500 rounded-full whitespace-nowrap">
            <span className="flex items-center gap-1 px-2">✨ Most Popular</span>
          </Badge>
        </div>
      )}

      {/* Background emoji layer */}
      {shouldShowPopular && (
        <div className="absolute inset-0 -m-6 z-[2]">
          <div className="absolute -top-3 -left-3 text-2xl rotate-12">🎉</div>
          <div className="absolute -top-2 -right-3 text-2xl -rotate-12">✨</div>
          <div className="absolute -bottom-2 -left-3 text-2xl -rotate-12">✨</div>
          <div className="absolute -bottom-3 -right-3 text-2xl rotate-12">🎉</div>
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 blur-xl -" />
        </div>
      )}

      {/* Content layer */}
      <div className="relative z-[3]">
        <div className="flex flex-col p-6 bg-transparent rounded-lg shadow-lg border border-gray-200 h-full text-white">
          <h3 className="text-xl font-bold mb-4 text-center">{title}</h3>
          <ul className="text-white mb-4 space-y-2">
            {description.map((item, index) => (
              <li key={index} className="flex items-start">
                <span className="mr-2">•</span>
                <span className="text-white">{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-auto">
            <div className="mb-6">
              {isFree ? (
                <span className="text-3xl font-bold text-white">Free</span>
              ) : (
                <>
                  <span className="text-3xl font-bold text-white">${price}</span>
                  <span className="text-white ml-1">per month</span>
                </>
              )}
            </div>
            <div className="relative">
              <Button 
                onClick={handleClick}
                className={getButtonClassName()}
                variant={getButtonVariant()}
                disabled={isCurrentPlan}
              >
                {getButtonText()}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Upgrade = () => {
  const { user, fetchUser } = useUser();
  const [planToDowngrade, setPlanToDowngrade] = useState(null);
  const [isDowngrading, setIsDowngrading] = useState(false);
  const [upgradingPlan, setUpgradingPlan] = useState(null);
  const [error, setError] = useState(null);
  const [cancelReason, setCancelReason] = useState('too_expensive');

  const handleSubscribe = async (plan) => {
    try {
      setUpgradingPlan(plan);
      const response = await sendRequest('/payments', 'POST', { plan });
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Failed to start upgrade:', error);
    } finally {
      setUpgradingPlan(null);
    }
  };

  const handleDowngrade = (plan) => {
    setError(null);
    setPlanToDowngrade(plan);
    setCancelReason('too_expensive'); // Reset to default
  };

  const confirmDowngrade = async () => {
    if (isDowngrading) return;
    
    setIsDowngrading(true);
    setError(null);
    
    try {
      const response = await sendRequest('/payments', 'POST', { 
        plan: planToDowngrade,
        cancel_reason: cancelReason 
      });
      if (response.error) {
        console.error('Failed to downgrade subscription:', response.error);
        setError(response.error || 'Failed to downgrade subscription. Please try again.');
        return;
      }
      if (response.url) {
        window.location.href = response.url;
      }
      await fetchUser();
      setPlanToDowngrade(null);
    } catch (error) {
      console.error('Failed to downgrade:', error);
      setError(error.message || 'Failed to downgrade subscription. Please try again.');
    } finally {
      setIsDowngrading(false);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen gap-4">
        <p className="text-red-500 font-medium">{error}</p>
        <Button 
          variant="outline" 
          onClick={() => window.location.href = '/upgrade'}
        >
          Go back
        </Button>
      </div>
    );
  }

  if (!user) {
    return (
      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
    );
  }

  const currentPlan = user.plan || 'free';
  const currentPlanTier = PLAN_TIERS[currentPlan];
  const totalVideos = user.total_unique_videos || 0;

  const getDowngradeWarning = (targetPlan) => {
    const excessVideos = totalVideos - user.plan_limit_for_downgrade;
    if (targetPlan === 'free') {
      return { 
        title: 'You are downgrading to free',
        description: error || `You will lose access to the ${user.plan} plan at the end of your billing period`,
        isError: !!error
      };
    }
    if (excessVideos <= 0) return { 
      title: 'Are you sure?',
      description: error || `You are downgrading to ${targetPlan}`,
      isError: !!error
    };
    return {
      title: excessVideos > 0 ? `You will lose ${excessVideos} video${excessVideos !== 1 ? 's' : ''} from your backup` : 'Are you sure?',
      description: error || `You are downgrading to ${targetPlan}`,
      isError: !!error
    };
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-8 pt-24 text-white">
      <div className="mb-16 text-center">
        <h2 className="text-2xl font-bold text-white mb-2">Your Plan</h2>
        <p className="text-white">
          You currently have <span className="font-semibold bg-gradient-to-r from-purple-700 via-purple-600 to-pink-600 animate-gradient bg-[length:200%_auto] bg-clip-text text-transparent">{user.total_downloaded_videos}</span> videos in your library
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <PricingCard
          title="Basic"
          description={[
            "Backup 30 videos",
            "Unlimited social accounts",
            "Manual backup"
          ]}
          price="0"
          onDowngrade={() => handleDowngrade('free')}
          isCurrentPlan={currentPlan === 'free'}
          planTier={PLAN_TIERS.free}
          currentPlanTier={currentPlanTier}
          isFree={true}
          isLoading={isDowngrading && planToDowngrade === 'free'}
        />
        <PricingCard
          title="Starter"
          description={[
            "Backup 100 videos",
            "Unlimited social accounts",
            "Manual backup"
          ]}
          price="5"
          onSubscribe={() => handleSubscribe('starter')}
          onDowngrade={() => handleDowngrade('starter')}
          isCurrentPlan={currentPlan === 'starter'}
          planTier={PLAN_TIERS.starter}
          currentPlanTier={currentPlanTier}
          isPopular={true}
          isLoading={upgradingPlan === 'starter' || (isDowngrading && planToDowngrade === 'starter')}
        />
        <PricingCard
          title="Pro"
          description={[
            "Backup 400 videos",
            "Unlimited social accounts",
            "Ongoing sync"
          ]}
          price="25"
          onSubscribe={() => handleSubscribe('pro')}
          onDowngrade={() => handleDowngrade('pro')}
          isCurrentPlan={currentPlan === 'pro'}
          planTier={PLAN_TIERS.pro}
          currentPlanTier={currentPlanTier}
          isLoading={upgradingPlan === 'pro' || (isDowngrading && planToDowngrade === 'pro')}
        />
        <PricingCard
          title="Superstar"
          description={[
            "Unlimited videos",
            "Unlimited social accounts",
            "Ongoing sync"
          ]}
          price="99"
          onSubscribe={() => handleSubscribe('superstar')}
          onDowngrade={() => handleDowngrade('superstar')}
          isCurrentPlan={currentPlan === 'superstar'}
          planTier={PLAN_TIERS.superstar}
          currentPlanTier={currentPlanTier}
          isLoading={upgradingPlan === 'superstar' || (isDowngrading && planToDowngrade === 'superstar')}
        />
      </div>

      <ConfirmationModal
        isOpen={!!planToDowngrade}
        onClose={() => {
          setPlanToDowngrade(null);
          setError(null);
          setCancelReason('too_expensive');
        }}
        onConfirm={confirmDowngrade}
        {...getDowngradeWarning(planToDowngrade)}
        isLoading={isDowngrading}
        confirmText="Downgrade"
        confirmVariant="default"
        className="bg-red-500 hover:bg-red-600 text-white"
      >
        {planToDowngrade === 'free' && (
          <div className="mt-4">
            <select
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-purple-500/30 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md"
              disabled={isDowngrading}
            >
              {CANCEL_REASONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        )}
      </ConfirmationModal>
    </div>
  );
};

export default Upgrade;
